/**
 *
 * AccordionContent
 * Subcomponent of Accordion.
 * Subcomponent: In this context, sub-components are defined as
 * components which have their own definition declared within
 * another parent component, and can only be used in the
 * context of that parent component
 *
 */

import React from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import { keyCodes } from "root/libs/constants/src";
import { breakpoints } from "root/libs/ui-styleguide/src";

const PanelCollapsibleStyles = css`
  height: 0;
  margin: 0;
  opacity: 0;
  overflow: hidden;
  position: relative;
  transform: scaleY(0);
  transform-origin: top;
  transition: transform cubic-bezier(0.4, 0.18, 0.41, 0.87) 200ms;
  will-change: transform;

  .accordion,
  .link {
    display: none;
  }

  &.expanded {
    border-bottom: 0;
    border-top: 0;
    height: auto;
    opacity: 1;
    overflow: visible;
    transform: scaleY(1);

    .accordion,
    .link {
      display: block;
    }
  }

  @media print {
    opacity: 1;
    height: 100%;
    transform: scaleY(1);

    .accordion,
    .link {
      display: block;
    }
  }

  @media screen and (min-width: ${breakpoints.tablet}) {
    &.expanded {
      margin-bottom: 0;
    }
  }

  ${(props) => props.extraStyles};
`;

const CollapsiblePanelWrapper = styled.div`
  ${PanelCollapsibleStyles}
  display: "block";
  padding: 2.1rem 2.1rem 30px 2.1rem;
  z-index: 2;
`;

const PanelWrapper = styled.div`
  font-stretch: normal;
  font-size: var(--dui-size-font-md);
  font-style: normal;
  font-weight: 400;
`;

const AccordionContent = ({
  id,
  labeledBy,
  children,
  className,
  isExpanded,
  extraStyles,
  dataTestid,
  onClick: onClickProp,
}) => {
  const onClick = (event) => {
    const { target } = event;
    const wrapsALink = target && target.href;
    const isAFormControl =
      target &&
      (target.tagName === "LABEL" ||
        target.tagName === "INPUT" ||
        target.tagName === "SELECT");

    if (!wrapsALink && !isAFormControl) {
      event.preventDefault();
      onClickProp(event);
    }
  };

  const onKeyPress = (event) => {
    const { keyCode, target } = event;
    const wrapsALink = target && target.href;

    if (
      !wrapsALink &&
      (keyCode === keyCodes.RETURN ||
        keyCode === keyCodes.SPACE ||
        keyCode === keyCodes.DOWN ||
        keyCode === keyCodes.UP)
    ) {
      event.preventDefault();
    }

    if (keyCode === keyCodes.RETURN || keyCode === keyCodes.SPACE) {
      onClickProp(event);
    }
  };

  const expandedClass = isExpanded ? "expanded" : "";

  return (
    <CollapsiblePanelWrapper
      id={id}
      role="region"
      aria-labelledby={labeledBy}
      aria-hidden={!isExpanded}
      className={`accordionPanel ${expandedClass} ${className}`}
      extraStyles={extraStyles}
      onClick={onClick}
      onKeyDown={onKeyPress}
      data-testid={dataTestid}
    >
      {isExpanded && <PanelWrapper>{children}</PanelWrapper>}
    </CollapsiblePanelWrapper>
  );
};

AccordionContent.propTypes = {
  children: PropTypes.node.isRequired,
  id: PropTypes.string.isRequired,
  labeledBy: PropTypes.string.isRequired,
  isExpanded: PropTypes.bool,
  extraStyles: PropTypes.array,
  onClick: PropTypes.func,
  className: PropTypes.string,
  dataTestid: PropTypes.string,
};

AccordionContent.defaultProps = {
  className: "",
  isExpanded: false,
  extraStyles: [],
  onClick: () => {},
  dataTestid: undefined,
};

export default AccordionContent;
